import { faTrashCan, faPencil } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Checkbox } from "primereact/checkbox";
import { useEffect, useState, useRef, useMemo } from "react";
import LoadingSpinner from "../../../components/loadingSpinner/LoadingSpinner";
import MessageModal, { MessageModalType } from "../../../components/modals/MessageModal";
import useFetch from "../../../hooks/useFetch";
import { getFetchOptions } from "../../../utils/fetch";
import AuthLogoutMessage from "../../../features/Auth/components/AuthLogoutMessage";
import { LogoutReason, useSession } from "../../../features/Auth/hooks/useSession";
import { getAdditionalOffersText, getPackageLevelJsx } from "../utilities";
import PackageEditorSection from "./PackageEditorSection";
import PackageSelectionSection from "./PackageSelectionSection";
import ProvisionListUsers from "../components/ProvisionListUsers";
import OrderSelectionSection from "../components/OrderSelectionSection";

const ProvisionCompany = ({ editing, packagesToAdd, setPackagesToAdd, setAddPackageValid, showSelectedPackages, setShowSelectedPackages, customer, offerRules,
	marketingConsent, setMarketingConsent, users, setUsers, setSubmitEnabled, addPackageValid, setValidatedModifiedUsers, setSelectedOrder, selectedOrder }) => {

	const { reason } = useSession();
	const [selectedPackage, setSelectedPackage] = useState("");
	const [selectedPackageRules, setSelectedPackageRules] = useState({});
	const { ready, execute, executingFetch } = useFetch();
	const [loadedUsers, setLoadedUsers] = useState([]);
	const [modifiedUsers, setModifiedUsers] = useState();
	const [selectedPackageObj, setSelectedPackageObj] = useState({
		packageId: -1,
		packageName: '',
		seats: 0,
		seatBlockSize: 0,
		guruByUser: false,
		packageLevel: -1,
		additionalOffers: [],
	});
	const [fatalError, setFatalError] = useState(false);
	const [dropdownOptions, setDropdownOptions] = useState([]);
	const [resendConfirmationUser, setResendConfirmationUser] = useState("");
	const [editingPackageIndex, setEditingPackageIndex] = useState(null);
	const [editingPackageName, setEditingPackageName] = useState(null);
	const [hasAnyCustomerAdmin, setHasAnyCustomerAdmin] = useState(true);
	const [hasCrmPrimaryContactSet, setHasCrmPrimaryContactSet] = useState(true);
	const originalPackagesRef = useRef(null);

	// Fetch data effect
	useEffect(() => {
		if (ready && customer) {
			const options = getFetchOptions({
				method: "GET"
			});
			const crmContactsOnly = !editing;
			execute("/api/Provisioning/Contacts/" + customer.accountid + "/" + crmContactsOnly, options, (error, data) => {
				if (error) {
					setFatalError(true);
					console.error(error.message);
				}
				else {
					setFatalError(false);
					setLoadedUsers(data || { Users: [] });
					if (editing) {
						const provisionedUsers = data.filter(x => x.Provisioned);
						setUsers(provisionedUsers);
					}
				}
			});
		}
	}, [customer, ready, execute, editing]);

	// Update add package validity effect
	useEffect(() => {
		const isValid =
			selectedPackageObj.packageName !== '' &&
			selectedPackageObj.seats > 0 &&
			((!selectedPackageRules.Levels.length) || (selectedPackageObj.packageLevel !== -1));

		setAddPackageValid(isValid);
	}, [selectedPackageObj, selectedPackageRules.Levels, setAddPackageValid]);

	useEffect(() => {
		if (originalPackagesRef.current === null) {
			originalPackagesRef.current = JSON.stringify(packagesToAdd ?? []);
		}
	}, [packagesToAdd]);

	const hasPackageChanges = useMemo(() => {
		if (originalPackagesRef.current === null) {
			return false;
		}
		return originalPackagesRef.current !== JSON.stringify(packagesToAdd ?? []);
	}, [packagesToAdd]);

	const orderChanged = useMemo(() => {
		if (!editing) {
			return false;
		}
		const originalOrderId = customer?.order?.OrderId ?? null;
		const originalRowKey = customer?.order?.TableRowKey ?? null;

		const selectedOrderId = selectedOrder?.OrderId ?? null;
		const selectedRowKey = selectedOrder?.TableRowKey ?? null;

		return (
			originalOrderId !== selectedOrderId ||
			originalRowKey !== selectedRowKey
		);
	}, [editing, selectedOrder, customer]);

	// Handle selected package function
	const handleSelectedPackage = (value) => {
		const selectedPackageRules = offerRules.find(rule => rule.Id === value);
		setSelectedPackageRules(selectedPackageRules);
		setSelectedPackageObj(prevState => ({
			...prevState,
			packageId: value,
			packageName: selectedPackageRules.Name,
			seatBlockSize: selectedPackageRules.SeatBlockSize,
			guruByUser: selectedPackageRules.GuruByUser
		}));
		setSelectedPackage(value);
		if (selectedPackageRules.PreventMarketingConsent) {
			setMarketingConsent(false);
		}
		setShowSelectedPackages(false);
	};

	const handleSelectedOrder = (order) => {
		if (order) {
			setSelectedOrder({
				TableRowKey: order.RowKey,
				OrderId: order.str_sageorderid
			});
		}
		else {
			setSelectedOrder(null);
		}
	};

	const resetSelectedPackageObj = () => {
		setSelectedPackageObj({
			packageId: -1,
			packageName: '',
			seats: 0,
			seatBlockSize: 0,
			guruByUser: false,
			packageLevel: -1,
			additionalOffers: [],
		})
	}

	const handleAddPackage = () => {
		if (editingPackageIndex !== null) {
			setPackagesToAdd(currentPackages => {
				const updated = [...currentPackages];
				updated[editingPackageIndex] = selectedPackageObj;
				return updated;
			});
		} else {
			setPackagesToAdd(currentPackages => [...currentPackages, selectedPackageObj]);
		}

		resetSelectedPackageObj();
		setShowSelectedPackages(true);
		setSelectedPackage(null);
		setEditingPackageIndex(null);
	}

	const handleUpdatePackage = (item, index) => {
		setSelectedPackageObj(item);
		setSelectedPackage(item.packageId);
		setShowSelectedPackages(false);
		setEditingPackageIndex(index);
		setEditingPackageName(item.packageName);

		const rules = offerRules.find(rule => rule.Id === item.packageId);
		if (rules) {
			setSelectedPackageRules(rules);
		}
	}

	// Submit validation
	useEffect(() => {
		const packagesLength = packagesToAdd?.length ?? 0;
		const usersLength = modifiedUsers?.length ?? 0;

		const enabled = editing
			? ((packagesLength > 0 && hasPackageChanges) || usersLength > 0 || orderChanged) && hasAnyCustomerAdmin && hasCrmPrimaryContactSet
			: (packagesLength > 0 && usersLength > 0 && hasAnyCustomerAdmin && hasCrmPrimaryContactSet);

		setSubmitEnabled(enabled);

		if (enabled) {
			setValidatedModifiedUsers(modifiedUsers);
		}
	}, [
		editing,
		packagesToAdd,
		hasPackageChanges,
		hasAnyCustomerAdmin,
		modifiedUsers,
		setSubmitEnabled,
		setValidatedModifiedUsers,
		orderChanged
	]);

	// handleResendEmail
	const handleResendEmail = (rowData) => {
		if (ready) {
			const options = getFetchOptions({
				method: "POST",
				body: rowData.Username
			});
			execute("/api/Provisioning/ResendRegistrationEmail/", options, (error, data) => {
				if (error) {
					setFatalError(true);
					console.error(error.message);
				}
				else {
					setResendConfirmationUser(rowData.Username);
				}
			});
		}
	};

	const handleMarketingConsentChange = (event) => {
		setMarketingConsent(event.checked);
	};

	const handleDeletePackage = (indexToRemove) => {
		const newPackages = packagesToAdd.filter((_, index) => index !== indexToRemove);
		setPackagesToAdd(newPackages);
		if (newPackages.length < 1) {
			setSelectedPackage("");
			setShowSelectedPackages(false);
		}
	};

	const handleCancel = () => {
		resetSelectedPackageObj(); // clear the fields
		setSelectedPackage("");
		setShowSelectedPackages(Boolean(packagesToAdd.length));
		setEditingPackageIndex(null);
	};

	const handleResendConfirmation = () => {
		setResendConfirmationUser("");
	};

	useEffect(() => {
		const updatedOptions = offerRules.filter(option =>
			!packagesToAdd.some(packageItem => packageItem.packageId === option.Id)
		);
		setDropdownOptions(updatedOptions);
	}, [packagesToAdd, offerRules]);

	const ResendModal = (
		<MessageModal title="Confirmation" type={MessageModalType.Information} size="m" onClick={handleResendConfirmation}>
			The registration email has been resent to '{resendConfirmationUser}'.
		</MessageModal>
	);

	const PackageListSection = (
		<>
			{showSelectedPackages && (
				<div>
					<h4 className="title-top-margin">Packages</h4>
					<div className="list-table-header">
						<p>Package</p>
						<p>Seats</p>
					</div>
					{packagesToAdd.map((item, packageIndex) => (
						<div className="list-table-item" key={packageIndex}>
							<div className="p-with-div-nesting font_s">
								{item.packageName}
								{getPackageLevelJsx(offerRules, item.additionalOffers && item.additionalOffers.length, item.packageId, item.packageLevel)}
								{getAdditionalOffersText(item.additionalOffers)}
							</div>
							<p>{item.seats} x {item.seatBlockSize}</p>
							<button onClick={() => handleUpdatePackage(item, packageIndex)} style={{ backgroundColor: 'none' }}><FontAwesomeIcon icon={faPencil} /></button>
							<button onClick={() => handleDeletePackage(packageIndex)} style={{ backgroundColor: 'none' }}><FontAwesomeIcon icon={faTrashCan} /></button>
						</div>
					))}
				</div>
			)}
		</>
	);

	const MarketingConsentSection = (
		<>
			<h4 className="title-top-margin table-title">Marketing Consent</h4>
			<div className="title-top-margin">
				<div>
					<Checkbox checked={marketingConsent} onChange={handleMarketingConsentChange} />
					<label style={{ marginLeft: '10px' }}>Enable marketing consent</label>
				</div>
			</div>
		</>
	);

	const render = () => {
		if (reason !== LogoutReason.None) {
			return <AuthLogoutMessage reason={reason} />;
		}
		if (executingFetch) {
			return <LoadingSpinner active={executingFetch} />;
		}
		if (resendConfirmationUser.length) {
			return ResendModal;
		}
		return (
			<>
				<OrderSelectionSection
					orders={customer.order_customer_accounts}
					selectedOrder={selectedOrder}
					handleSelectedOrder={handleSelectedOrder}
				/>
				{editingPackageIndex == null ? (
					<PackageSelectionSection
						offerRules={offerRules}
						showSelectedPackages={showSelectedPackages}
						dropdownOptions={dropdownOptions}
						selectedPackage={selectedPackage}
						handleSelectedPackage={handleSelectedPackage}
					/>
				) : (
					<h2>Editing Package: {editingPackageName}</h2>
				)}

				<PackageEditorSection
					selectedPackage={selectedPackage}
					selectedPackageObj={selectedPackageObj}
					setSelectedPackageObj={setSelectedPackageObj}
					selectedPackageRules={selectedPackageRules}
					handleCancel={handleCancel}
					handleAddPackage={handleAddPackage}
					addPackageValid={addPackageValid}
					editingPackageIndex={editingPackageIndex}
				/>
				{PackageListSection}
				{MarketingConsentSection}
				<ProvisionListUsers
					fatalError={fatalError}
					editing={editing}
					users={loadedUsers}
					onResendEmail={handleResendEmail}
					onAdminValidationChange={setHasAnyCustomerAdmin}
					setModifiedUsers={setModifiedUsers}
					onPrimaryContactValidationChange={setHasCrmPrimaryContactSet}
				/>

			</>);
	}

	return (
		<>{render()}</>
	);
};

export default ProvisionCompany;